import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, MessageSquare, Shield } from 'lucide-react';
import SiteLogo from '../../components/Layout/SiteLogo';
import LanguageSwitcher from '../../components/Layout/LanguageSwitcher';
import RichTextContent from '../../components/RichTextContent';
import { OpcaoCalculada, formatEuro } from '../../utils/orcamentoPagamento';

type LocaleCode = 'pt' | 'en' | 'es';

interface LocaleBlock {
  descricao?: string;
  corpoTexto?: string;
}

interface OrcamentoItem {
  descricao: string;
  i18n?: { pt?: LocaleBlock; en?: LocaleBlock; es?: LocaleBlock };
  valor: number;
  quantidade: number;
  abatidoAoFecharOutros?: boolean;
}

const pageShell = 'min-h-[100dvh] bg-slate-100';

// Espelho de MOTIVOS_RECUSA em backend/models/Orcamento.js
const MOTIVOS_RECUSA = ['preco', 'condicoes_pagamento', 'outro_fornecedor', 'adiou', 'desistiu', 'outro'] as const;
type MotivoRecusa = (typeof MOTIVOS_RECUSA)[number];

function pick(block: LocaleBlock | undefined, ptBlock: LocaleBlock | undefined, field: keyof LocaleBlock, fallback: string): string {
  return block?.[field]?.trim() || ptBlock?.[field]?.trim() || fallback;
}

const OrcamentoView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const activeLang = (i18n.language?.split('-')[0] || 'pt') as LocaleCode;

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const [alreadyDeclined, setAlreadyDeclined] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [nomeCliente, setNomeCliente] = useState('');
  const [itens, setItens] = useState<OrcamentoItem[]>([]);
  const [formaPagamento, setFormaPagamento] = useState('');
  const [corpoTextoI18n, setCorpoTextoI18n] = useState<{ pt?: LocaleBlock; en?: LocaleBlock; es?: LocaleBlock }>({});
  const [subtotal, setSubtotal] = useState(0);
  const [abatimento, setAbatimento] = useState(0);
  const [abatimentoDescricao, setAbatimentoDescricao] = useState('');
  const [abatimentoItens, setAbatimentoItens] = useState(0);
  const [total, setTotal] = useState(0);
  const [opcoes, setOpcoes] = useState<OpcaoCalculada[]>([]);
  const [opcaoId, setOpcaoId] = useState('');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  const [nomeCompleto, setNomeCompleto] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);

  const [showDecline, setShowDecline] = useState(false);
  const [motivoRecusa, setMotivoRecusa] = useState<MotivoRecusa | ''>('');
  const [comentarioRecusa, setComentarioRecusa] = useState('');
  const [declining, setDeclining] = useState(false);
  const [declineError, setDeclineError] = useState('');
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorMsg(t('orcamentoForm.linkInvalid'));
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`/orcamento/public/${token}`);
        setValid(res.data.valid);
        setAlreadyAccepted(res.data.alreadyAccepted);
        setAlreadyDeclined(!!res.data.alreadyDeclined);
        setNomeCliente(res.data.nomeCliente || '');
        setItens(res.data.itens || []);
        setFormaPagamento(res.data.formaPagamento || '');
        setCorpoTextoI18n(res.data.i18n || { pt: { corpoTexto: res.data.corpoTexto || '' } });
        setSubtotal(res.data.subtotal || 0);
        setAbatimento(res.data.abatimento || 0);
        setAbatimentoDescricao(res.data.abatimentoDescricao || '');
        setAbatimentoItens(res.data.abatimentoItens || 0);
        setTotal(res.data.total || 0);
        setOpcoes(res.data.opcoes || []);
        if (res.data.opcoes?.length === 1) setOpcaoId(res.data.opcoes[0].id);
        setExpiresAt(res.data.expiresAt || null);
      } catch (err: unknown) {
        setValid(false);
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          t('orcamentoForm.linkExpired');
        setErrorMsg(msg);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const corpoTexto = useMemo(
    () => pick(corpoTextoI18n[activeLang], corpoTextoI18n.pt, 'corpoTexto', ''),
    [corpoTextoI18n, activeLang]
  );

  const resolvedItens = useMemo(
    () =>
      itens.map((item) => ({
        ...item,
        descricao: pick(item.i18n?.[activeLang], item.i18n?.pt, 'descricao', item.descricao)
      })),
    [itens, activeLang]
  );

  const labelFormaPagamento = (forma?: string) =>
    t(`orcamentoForm.formaPagamento.${forma}`, { defaultValue: forma || '—' });

  const labelOpcao = (o: OpcaoCalculada) => {
    if (o.tipo === 'avista') return t('orcamentoForm.optionAvista');
    if (o.tipo === 'avista_desconto') return t('orcamentoForm.optionAvistaDesconto', { pct: o.percentual });
    return t('orcamentoForm.optionParcelado', { n: o.parcelas, valor: formatEuro(o.valorParcela) });
  };

  const detalheOpcao = (o: OpcaoCalculada) => {
    if (o.tipo === 'parcelado') {
      return o.percentual > 0
        ? t('orcamentoForm.optionParceladoTotal', { total: formatEuro(o.valorFinal), pct: o.percentual })
        : t('orcamentoForm.semJuros');
    }
    return '';
  };

  const handleSubmit = async () => {
    setSubmitError('');
    if (opcoes.length > 0 && !opcaoId) {
      setSubmitError(t('orcamentoForm.optionRequired'));
      return;
    }
    if (!consentAccepted) {
      setSubmitError(t('orcamentoForm.consentRequired'));
      return;
    }
    if (!nomeCompleto.trim()) {
      setSubmitError(t('orcamentoForm.nameRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`/orcamento/public/${token}/aceitar`, {
        nomeCompleto: nomeCompleto.trim(),
        consentAccepted: true,
        ...(opcoes.length > 0 ? { opcaoId } : {})
      });
      setDone(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('orcamentoForm.submitError');
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setDeclineError('');
    if (!motivoRecusa) {
      setDeclineError(t('orcamentoForm.declineReasonRequired'));
      return;
    }
    if (motivoRecusa === 'outro' && !comentarioRecusa.trim()) {
      setDeclineError(t('orcamentoForm.declineDescribeRequired'));
      return;
    }
    setDeclining(true);
    try {
      await axios.post(`/orcamento/public/${token}/recusar`, {
        motivo: motivoRecusa,
        comentario: comentarioRecusa.trim()
      });
      setDeclined(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('orcamentoForm.declineError');
      setDeclineError(msg);
    } finally {
      setDeclining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label={t('common.loading')} />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <Shield className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('orcamentoForm.linkUnavailable')}</h1>
          <p className="text-gray-600">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (alreadyDeclined || declined) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('orcamentoForm.declinedTitle')}</h1>
          <p className="text-gray-600">
            {declined ? t('orcamentoForm.declinedMsg') : t('orcamentoForm.alreadyDeclinedMsg')}
          </p>
        </div>
      </div>
    );
  }

  if (alreadyAccepted || done) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('orcamentoForm.acceptedTitle')}</h1>
          <p className="text-gray-600">
            {done ? t('orcamentoForm.acceptedMsg') : t('orcamentoForm.alreadyAcceptedMsg')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${pageShell} pb-[max(1rem,env(safe-area-inset-bottom))]`}>
      <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 sm:py-8 lg:py-10">
        <header className="text-center text-gray-900 mb-5 sm:mb-8">
          <div className="flex justify-center mb-3 sm:mb-4">
            <SiteLogo size="lg" nameClassName="font-bold text-gray-900 text-xl sm:text-2xl" className="justify-center" />
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight px-1">{t('orcamentoForm.title')}</h1>
          {nomeCliente && (
            <p className="text-gray-600 text-sm sm:text-base mt-2">{t('orcamentoForm.greeting', { nome: nomeCliente })}</p>
          )}
          {expiresAt && (
            <p className="text-gray-400 text-xs mt-1">
              {t('orcamentoForm.expiresUntil', { data: new Date(expiresAt).toLocaleDateString(i18n.language) })}
            </p>
          )}
          <div className="flex justify-center mt-4 lg:hidden">
            <LanguageSwitcher compact variant="floating" className="shadow-md" />
          </div>
        </header>

        <div className="flex flex-col items-stretch lg:flex-row lg:items-start lg:justify-center lg:gap-5 xl:gap-8">
          <div className="w-full max-w-2xl mx-auto lg:mx-0 min-w-0 flex-1">
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">{t('orcamentoForm.itemsHeading')}</h2>
              <div className="divide-y divide-gray-100">
                {resolvedItens.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 text-sm">
                    <div>
                      <p className="text-gray-900">{item.descricao}</p>
                      {item.quantidade > 1 && (
                        <p className="text-xs text-gray-500">
                          {item.quantidade} × €{Number(item.valor).toFixed(2)}
                        </p>
                      )}
                    </div>
                    <p className="font-medium text-gray-900">
                      €{(Number(item.valor) * Number(item.quantidade || 1)).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
              {abatimento > 0 && (
                <div className="pt-3 mt-2 border-t border-gray-200 space-y-1 text-sm">
                  <div className="flex justify-between items-center">
                    <p className="text-gray-600">{t('orcamentoForm.subtotal')}</p>
                    <p className="text-gray-900">€{subtotal.toFixed(2)}</p>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <p className="text-green-700">
                      {t('orcamentoForm.abatimento')}
                      {(() => {
                        const origem = [
                          ...(abatimentoItens > 0
                            ? resolvedItens.filter((item) => item.abatidoAoFecharOutros).map((item) => item.descricao)
                            : []),
                          ...(abatimentoDescricao ? [abatimentoDescricao] : [])
                        ];
                        return origem.length > 0 ? (
                          <span className="block text-xs text-gray-500">{origem.join(', ')}</span>
                        ) : null;
                      })()}
                    </p>
                    <p className="text-green-700 whitespace-nowrap">−€{abatimento.toFixed(2)}</p>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center pt-3 mt-2 border-t border-gray-200">
                <p className="font-semibold text-gray-900">{t('orcamentoForm.total')}</p>
                <p className="font-bold text-lg text-gray-900">€{total.toFixed(2)}</p>
              </div>
              {opcoes.length > 0 ? (
                <fieldset className="mt-5">
                  <legend className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
                    {t('orcamentoForm.paymentOptionsHeading')}
                  </legend>
                  <div className="space-y-2">
                    {opcoes.map((o) => {
                      const selected = opcaoId === o.id;
                      const detalhe = detalheOpcao(o);
                      return (
                        <label
                          key={o.id}
                          className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border cursor-pointer transition-colors ${
                            selected ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="opcao-pagamento"
                            checked={selected}
                            onChange={() => setOpcaoId(o.id)}
                            className="shrink-0"
                          />
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm sm:text-base font-medium text-gray-900">{labelOpcao(o)}</span>
                            {detalhe && <span className="block text-xs text-gray-500 mt-0.5">{detalhe}</span>}
                          </span>
                          <span className="text-right shrink-0">
                            {o.tipo === 'avista_desconto' && o.valorFinal < o.valorBase && (
                              <span className="block text-xs text-gray-400 line-through">{formatEuro(o.valorBase)}</span>
                            )}
                            <span className="block font-semibold text-gray-900">{formatEuro(o.valorFinal)}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ) : (
                <p className="text-sm text-gray-600 mt-3">
                  {t('orcamentoForm.paymentLabel')}: {labelFormaPagamento(formaPagamento)}
                </p>
              )}

              {corpoTexto && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <RichTextContent html={corpoTexto} className="text-sm text-gray-700" />
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">{t('orcamentoForm.acceptHeading')}</h2>

              <label className="block">
                <span className="text-sm text-gray-600">{t('orcamentoForm.fullName')}</span>
                <input
                  type="text"
                  value={nomeCompleto}
                  onChange={(e) => setNomeCompleto(e.target.value)}
                  placeholder={t('orcamentoForm.fullNamePh') as string}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </label>

              <label className="flex items-start gap-2 mt-4 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={consentAccepted}
                  onChange={(e) => setConsentAccepted(e.target.checked)}
                  className="mt-1"
                />
                <span>{t('orcamentoForm.consentAccept')}</span>
              </label>

              {submitError && <p className="text-sm text-red-600 mt-3">{submitError}</p>}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-60"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('orcamentoForm.submit')}
              </button>
            </div>

            {!showDecline ? (
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setShowDecline(true)}
                  className="text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700"
                >
                  {t('orcamentoForm.declineToggle')}
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mt-4 sm:mt-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('orcamentoForm.declineHeading')}</h2>
                <p className="text-sm text-gray-500 mt-1 mb-3">{t('orcamentoForm.declineIntro')}</p>

                <fieldset className="space-y-2">
                  <legend className="sr-only">{t('orcamentoForm.declineHeading')}</legend>
                  {MOTIVOS_RECUSA.map((m) => (
                    <label
                      key={m}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors text-sm ${
                        motivoRecusa === m ? 'border-gray-500 bg-gray-50 ring-1 ring-gray-500' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="motivo-recusa"
                        checked={motivoRecusa === m}
                        onChange={() => setMotivoRecusa(m)}
                        className="shrink-0"
                      />
                      <span className="text-gray-900">{t(`orcamentoForm.declineReasons.${m}`)}</span>
                    </label>
                  ))}
                </fieldset>

                <label className="block mt-4">
                  <span className="text-sm text-gray-600">
                    {motivoRecusa === 'outro' ? t('orcamentoForm.declineCommentRequired') : t('orcamentoForm.declineComment')}
                  </span>
                  <textarea
                    value={comentarioRecusa}
                    onChange={(e) => setComentarioRecusa(e.target.value)}
                    maxLength={1000}
                    rows={3}
                    placeholder={t('orcamentoForm.declineCommentPh') as string}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                {declineError && <p className="text-sm text-red-600 mt-3">{declineError}</p>}

                <div className="flex flex-col-reverse sm:flex-row gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDecline(false);
                      setDeclineError('');
                    }}
                    className="sm:flex-1 px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                  >
                    {t('orcamentoForm.declineCancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDecline}
                    disabled={declining}
                    className="sm:flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gray-800 text-white font-medium hover:bg-gray-900 disabled:opacity-60"
                  >
                    {declining && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t('orcamentoForm.declineSubmit')}
                  </button>
                </div>
              </div>
            )}
          </div>

          <aside className="hidden lg:flex shrink-0 sticky top-8 self-start flex-col items-center">
            <LanguageSwitcher orientation="vertical" variant="floating" compact />
          </aside>
        </div>
      </div>
    </div>
  );
};

export default OrcamentoView;
